<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { api, extractError } from '@/api/client'
import Modal from '@/components/ui/Modal.vue'
import { preloadDigiseller } from '@/lib/digisellerLoader'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()

type OptionType = 'server' | 'renewal' | 'subscription'

interface PurchaseLink {
  label: string
  url: string
}
interface PurchaseOption {
  widget_product_id: string
  links: PurchaseLink[]
}
interface CatalogServer {
  id: number
  name: string
  display_location?: string
  location: string
  country_code?: string
  protocols_text?: string
}
interface CatalogStats {
  servers: number
  locations: number
  protocols: number
  keys: number
  configs: number
}

const optionsLoading = ref(false)
const optionsLoaded = ref(false)
const error = ref('')
const agentId = ref('')
const options = ref<Record<OptionType, PurchaseOption>>({
  server: { widget_product_id: '', links: [] },
  renewal: { widget_product_id: '', links: [] },
  subscription: { widget_product_id: '', links: [] },
})
const catalog = ref<CatalogServer[]>([])
const catalogStats = ref<CatalogStats | null>(null)

const step = ref(0)
const selectedType = ref<OptionType | null>(null)
const buyMethod = ref<'widget' | 'site' | null>(null)

const typeMeta: Record<OptionType, { title: string; short: string }> = {
  server: { title: 'На отдельный сервер', short: 'Код для одного сервера' },
  renewal: { title: 'Код продления', short: 'Продлить свободный сервер' },
  subscription: { title: 'Полная подписка', short: 'Все локации и протоколы' },
}

const currentOption = computed<PurchaseOption | null>(() =>
  selectedType.value ? options.value[selectedType.value] : null,
)
const hasWidget = computed(() => !!currentOption.value?.widget_product_id)
const hasLinks = computed(() => (currentOption.value?.links.length ?? 0) > 0)

const progressSteps = computed(() => ['Тип', 'Описание', 'Покупка'])

async function loadOptions(silent = false) {
  if (!silent) {
    optionsLoading.value = !optionsLoaded.value
  }
  error.value = ''
  try {
    const { data } = await api.get('/api/user/purchase-options')
    if (!data.success) {
      error.value = data.message || 'Не удалось загрузить опции покупки'
      return
    }
    agentId.value = data.digiseller_agent_id || ''
    options.value = {
      server: data.options?.server ?? { widget_product_id: '', links: [] },
      renewal: data.options?.renewal ?? { widget_product_id: '', links: [] },
      subscription: data.options?.subscription ?? { widget_product_id: '', links: [] },
    }
    catalog.value = data.subscription_catalog?.servers ?? []
    catalogStats.value = data.subscription_catalog?.stats ?? null
    optionsLoaded.value = true
  } catch (e) {
    error.value = extractError(e)
  } finally {
    optionsLoading.value = false
  }
}

function reset() {
  step.value = 0
  selectedType.value = null
  buyMethod.value = null
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      reset()
      void loadOptions(optionsLoaded.value)
    }
  },
)

function chooseType(t: OptionType) {
  selectedType.value = t
  step.value = 1
}

function goToBuyMethods() {
  step.value = 2
}

function chooseWidget() {
  const pid = currentOption.value?.widget_product_id
  if (!pid) return
  void preloadDigiseller(agentId.value || undefined)
  const q = agentId.value ? `?ai=${encodeURIComponent(agentId.value)}` : ''
  window.open(`/buy/${encodeURIComponent(pid)}${q}`, '_blank', 'noopener')
  emit('close')
}

function chooseSite() {
  buyMethod.value = 'site'
  step.value = 3
}

function back() {
  if (step.value === 3) {
    buyMethod.value = null
    step.value = 2
    return
  }
  if (step.value === 2) {
    step.value = 1
    return
  }
  if (step.value === 1) {
    selectedType.value = null
    step.value = 0
    return
  }
  emit('close')
}

function openSite(url: string) {
  window.open(url, '_blank', 'noopener')
}
</script>

<template>
  <Modal :open="open" title="Купить код активации" max-width="2xl" @close="emit('close')">
    <div class="min-h-[280px]">
      <div class="mb-5 flex items-center justify-center gap-2">
        <template v-for="(label, i) in progressSteps" :key="label">
          <div class="flex items-center gap-2">
            <span
              class="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold"
              :class="i <= step ? 'bg-primary text-white' : 'bg-white-light text-muted'"
            >
              {{ i + 1 }}
            </span>
            <span class="hidden text-xs sm:inline" :class="i <= step ? 'font-semibold text-primary' : 'text-muted'">
              {{ label }}
            </span>
          </div>
          <span v-if="i < progressSteps.length - 1" class="h-px w-5 bg-white-light" />
        </template>
      </div>

      <div v-if="optionsLoading" class="py-10 text-center text-sm text-muted">Загрузка…</div>
      <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

      <template v-else>
        <div v-if="step === 0" class="space-y-3">
          <p class="text-sm text-muted">Выберите, какой код активации вам нужен.</p>
          <button
            v-for="t in (['server', 'renewal', 'subscription'] as OptionType[])"
            :key="t"
            type="button"
            class="flex w-full items-center justify-between rounded-lg border border-white-light p-4 text-left transition hover:border-primary hover:bg-primary/5"
            @click="chooseType(t)"
          >
            <span>
              <span class="block font-bold">{{ typeMeta[t].title }}</span>
              <span class="block text-xs text-muted">{{ typeMeta[t].short }}</span>
            </span>
            <i class="bi bi-chevron-right text-muted" />
          </button>
        </div>

        <div v-else-if="step === 1 && selectedType" class="space-y-4 text-sm leading-relaxed text-[#1f2937]">
          <h3 class="text-base font-bold">{{ typeMeta[selectedType].title }}</h3>

          <template v-if="selectedType === 'server'">
            <p>
              Код для активации <strong>одного отдельного сервера</strong>. Код не привязан к конкретной
              локации.
            </p>
            <p>
              При активации вы сами выберете нужную <strong>локацию и сервер</strong> из доступных.
            </p>
          </template>

          <template v-else-if="selectedType === 'renewal'">
            <p>Код <strong>продления</strong> срока действия активированного ключа.</p>
            <div class="rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-800">
              ⚠️ Только для <strong>свободного сервера</strong>, не для полной подписки.
            </div>
          </template>

          <template v-else>
            <p>Вы активируете <strong>полную подписку</strong> — доступ ко всем серверам сразу.</p>
            <div v-if="catalogStats" class="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div class="rounded-lg bg-white-light/40 p-3 text-center">
                <div class="text-lg font-extrabold text-primary">{{ catalogStats.locations }}</div>
                <div class="text-xs text-muted">локаций</div>
              </div>
              <div class="rounded-lg bg-white-light/40 p-3 text-center">
                <div class="text-lg font-extrabold text-primary">{{ catalogStats.servers }}</div>
                <div class="text-xs text-muted">серверов</div>
              </div>
              <div class="rounded-lg bg-white-light/40 p-3 text-center">
                <div class="text-lg font-extrabold text-primary">{{ catalogStats.keys }}</div>
                <div class="text-xs text-muted">ключей</div>
              </div>
              <div class="rounded-lg bg-white-light/40 p-3 text-center">
                <div class="text-lg font-extrabold text-primary">{{ catalogStats.configs }}</div>
                <div class="text-xs text-muted">конфигураций</div>
              </div>
            </div>
          </template>

          <div class="pt-2">
            <button type="button" class="btn btn-primary w-full" @click="goToBuyMethods">Далее</button>
          </div>
        </div>

        <div v-else-if="step === 2 && selectedType" class="space-y-3">
          <p class="text-sm text-muted">Выберите способ покупки.</p>
          <button
            v-if="hasWidget"
            type="button"
            class="flex w-full items-center justify-between rounded-lg border border-white-light p-4 text-left transition hover:border-primary hover:bg-primary/5"
            @click="chooseWidget"
          >
            <span>
              <span class="block font-bold">Купить здесь</span>
              <span class="block text-xs text-muted">Страница товара в новой вкладке</span>
            </span>
            <i class="bi bi-box-arrow-up-right text-muted" />
          </button>
          <button
            v-if="hasLinks"
            type="button"
            class="flex w-full items-center justify-between rounded-lg border border-white-light p-4 text-left transition hover:border-primary hover:bg-primary/5"
            @click="chooseSite"
          >
            <span>
              <span class="block font-bold">Купить на сайте</span>
              <span class="block text-xs text-muted">Переход на сайт магазина</span>
            </span>
            <i class="bi bi-chevron-right text-muted" />
          </button>
        </div>

        <div v-else-if="step === 3 && selectedType && buyMethod === 'site'" class="space-y-3">
          <p class="text-sm text-muted">Выберите магазин — откроется в новой вкладке.</p>
          <button
            v-for="link in currentOption?.links || []"
            :key="link.url"
            type="button"
            class="flex w-full items-center justify-between rounded-lg border border-white-light p-3 text-left transition hover:border-primary hover:bg-primary/5"
            @click="openSite(link.url)"
          >
            <span class="font-semibold">{{ link.label }}</span>
            <i class="bi bi-box-arrow-up-right text-muted" />
          </button>
        </div>
      </template>
    </div>

    <template #footer>
      <button type="button" class="btn btn-outline-secondary" @click="back">
        {{ step === 0 ? 'Закрыть' : 'Назад' }}
      </button>
    </template>
  </Modal>
</template>
